// mindmetal-app/web/src/pages/employee/mindmetal/DistillationTesterTab.tsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Beaker, FileText, MessageSquare, Mic, Save, Copy, Check } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Textarea, Badge } from '@personic/shared'
import { SignalDiffViewer } from '@/components/mindmetal/SignalDiffViewer'
import { QuestionnaireUploader } from '@/components/mindmetal/QuestionnaireUploader'
import type { DistillationRequest, DistillationResult } from '@personic/shared'
import { api } from '@personic/shared'

const CONTENT_TYPES = [
  { value: 'questionnaire', label: 'Questionnaire (Q&A format)', icon: FileText },
  { value: 'monolog', label: 'Monolog (spoken text)', icon: Mic },
  { value: 'transcript', label: 'Transcript (conversation)', icon: MessageSquare },
] as const

const EXAMPLE_CONTENT: Record<string, string> = {
  questionnaire: `Q: How do you typically approach a new project?
A: I like to start by understanding the big picture and then break it down into smaller tasks. I prefer having a clear plan before diving in, but I'm also flexible when things change.

Q: How do you handle feedback on your work?
A: I appreciate constructive feedback because it helps me grow. I try not to take criticism personally and focus on what I can learn from it.

Q: Describe your ideal work environment.
A: I work best in a quiet space where I can focus deeply. I enjoy collaboration but also need time for independent work. I prefer clear expectations and regular check-ins.`,

  monolog: `I've been thinking a lot about how I approach problems lately. When I encounter something new, my first instinct is to gather as much information as possible. I'll read documentation, watch tutorials, and look at examples before I start working on anything.

Some people jump right in, but that's not really my style. I find that having a solid foundation helps me avoid mistakes later. Once I understand the fundamentals, I move pretty quickly.

I also like to set up my workspace just right. A clean desk, good lighting, maybe some instrumental music in the background. These details matter to me because they help me focus.`,

  transcript: `Person A: Hey, how did your presentation go yesterday?
Person B: It went really well, thanks for asking! I was nervous at first, but once I got into the material, I felt more confident.

Person A: That's great! Did they ask a lot of questions?
Person B: They did, but I had prepared for most of them. There was one question about scaling that caught me off guard, though.

Person A: How did you handle that?
Person B: I was honest and said I'd need to research it more. I think they appreciated the transparency rather than me making something up.`,
}

interface SavedTestCase {
  id: string
  name: string
  contentType: string
  content: string
  context: string
  tiers: string[]
  savedAt: string
}

export function DistillationTesterTab() {
  const [content, setContent] = useState('')
  const [contentType, setContentType] = useState<DistillationRequest['content_type']>('questionnaire')
  const [context, setContext] = useState('')
  const [tiers, setTiers] = useState<('universal' | 'behavioral')[]>(['universal', 'behavioral'])
  const [result, setResult] = useState<DistillationResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRawJson, setShowRawJson] = useState(false)
  const [copied, setCopied] = useState(false)
  const [testCaseName, setTestCaseName] = useState('')
  const [savedTestCases, setSavedTestCases] = useState<SavedTestCase[]>(() => {
    const saved = localStorage.getItem('distillation-test-cases')
    return saved ? JSON.parse(saved) : []
  })

  const toggleTier = (tier: 'universal' | 'behavioral') => {
    setTiers((prev) =>
      prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier]
    )
  }

  const loadExample = () => {
    setContent(EXAMPLE_CONTENT[contentType])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const response = await api.post<DistillationResult>(
        '/api/mindmetal/v3/admin/test-distillation',
        {
          content,
          content_type: contentType,
          context: context || undefined,
          tiers,
        }
      )
      setResult(response.data)
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Failed to run distillation'
      setError(detail)
    } finally {
      setIsLoading(false)
    }
  }

  const copyResult = async () => {
    if (!result) return
    await navigator.clipboard.writeText(JSON.stringify(result, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const saveTestCase = () => {
    if (!testCaseName.trim()) return

    const newTestCase: SavedTestCase = {
      id: Date.now().toString(),
      name: testCaseName,
      contentType,
      content,
      context,
      tiers,
      savedAt: new Date().toISOString(),
    }

    const updated = [newTestCase, ...savedTestCases.slice(0, 9)]
    setSavedTestCases(updated)
    localStorage.setItem('distillation-test-cases', JSON.stringify(updated))
    setTestCaseName('')
  }

  const loadTestCase = (testCase: SavedTestCase) => {
    setContentType(testCase.contentType as DistillationRequest['content_type'])
    setContent(testCase.content)
    setContext(testCase.context)
    setTiers(testCase.tiers as ('universal' | 'behavioral')[])
  }

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Input Panel */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Beaker className="w-5 h-5 text-copper-600" />
                Distillation Tester
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-midnight-700 mb-2">
                    Content Type
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {CONTENT_TYPES.map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setContentType(value)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          contentType === value
                            ? 'border-copper-500 bg-copper-50 text-copper-700'
                            : 'border-warm-200 hover:border-warm-300'
                        }`}
                      >
                        <Icon className="w-4 h-4 mb-1" />
                        <span className="text-xs block">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* File Upload (questionnaire mode) */}
                {contentType === 'questionnaire' && (
                  <div>
                    <label className="block text-sm font-medium text-midnight-700 mb-2">
                      Upload Questionnaire
                    </label>
                    <QuestionnaireUploader
                      onContentParsed={(parsedContent) => {
                        if (parsedContent) setContent(parsedContent)
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-midnight-700">Content</label>
                    <button
                      type="button"
                      onClick={loadExample}
                      className="text-xs text-copper-600 hover:text-copper-700"
                    >
                      Load Example
                    </button>
                  </div>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Enter content to distill..."
                    rows={12}
                    className="font-mono text-sm"
                    required
                  />
                </div>

                {/* Context */}
                <Input
                  label="Context (optional)"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Any additional context about this content..."
                />

                {/* Tiers */}
                <div>
                  <label className="block text-sm font-medium text-midnight-700 mb-2">
                    Signal Tiers
                  </label>
                  <div className="flex gap-2">
                    {(['universal', 'behavioral'] as const).map((tier) => (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => toggleTier(tier)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          tiers.includes(tier)
                            ? 'bg-copper-100 text-copper-700 ring-2 ring-copper-300'
                            : 'bg-warm-100 text-warm-500 hover:bg-warm-200'
                        }`}
                      >
                        {tier.charAt(0).toUpperCase() + tier.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  isLoading={isLoading}
                  disabled={!content.trim() || tiers.length === 0}
                  fullWidth
                >
                  Run Distillation
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Saved Test Cases */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Saved Test Cases</CardTitle>
            </CardHeader>
            <CardContent>
              {result && (
                <div className="mb-4 flex gap-2">
                  <Input
                    value={testCaseName}
                    onChange={(e) => setTestCaseName(e.target.value)}
                    placeholder="Test case name..."
                    className="text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={saveTestCase}
                    disabled={!testCaseName.trim()}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {savedTestCases.length === 0 ? (
                <p className="text-sm text-warm-400 text-center py-4">
                  No saved test cases yet
                </p>
              ) : (
                <div className="space-y-2">
                  {savedTestCases.map((tc) => (
                    <button
                      key={tc.id}
                      onClick={() => loadTestCase(tc)}
                      className="w-full text-left p-3 rounded-lg border border-warm-200 hover:border-copper-300 hover:bg-copper-50 transition-all"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-midnight-800 text-sm">{tc.name}</span>
                        <Badge variant="default" size="sm">
                          {tc.contentType}
                        </Badge>
                      </div>
                      <p className="text-xs text-warm-400">
                        {new Date(tc.savedAt).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Results */}
      {(result || error) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Results</CardTitle>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowRawJson(!showRawJson)}
                  className="text-sm text-warm-500 hover:text-midnight-700"
                >
                  {showRawJson ? 'Show Formatted' : 'Show Raw JSON'}
                </button>
                <Button variant="ghost" size="sm" onClick={copyResult}>
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  {error}
                </div>
              )}

              {result && (
                <>
                  {showRawJson ? (
                    <pre className="p-4 bg-midnight-900 text-warm-100 rounded-lg overflow-x-auto text-sm font-mono">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  ) : (
                    <div className="space-y-6">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-sage-50 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-sage-700">
                            {result.signals_extracted}
                          </div>
                          <div className="text-xs text-sage-600">Signals Extracted</div>
                        </div>
                        <div className="bg-copper-50 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-copper-700">
                            {result.preferences_extracted}
                          </div>
                          <div className="text-xs text-copper-600">Preferences</div>
                        </div>
                        <div className="bg-warm-50 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-midnight-700">
                            {result.new_observation_count}
                          </div>
                          <div className="text-xs text-warm-600">Observations</div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-sm font-bold text-blue-700 capitalize">
                            {result.new_coverage_level}
                          </div>
                          <div className="text-xs text-blue-600">Coverage Level</div>
                        </div>
                      </div>

                      {/* Signal Diffs */}
                      {result.signal_diffs.length > 0 && (
                        <div>
                          <h4 className="font-medium text-midnight-800 mb-3">Signal Changes</h4>
                          <SignalDiffViewer diffs={result.signal_diffs} />
                        </div>
                      )}

                      {/* Evidence Samples */}
                      {result.evidence_samples.length > 0 && (
                        <div>
                          <h4 className="font-medium text-midnight-800 mb-3">Evidence Samples</h4>
                          <div className="space-y-2">
                            {result.evidence_samples.map((sample, i) => (
                              <div
                                key={i}
                                className="p-3 bg-warm-50 rounded-lg text-sm text-midnight-700 font-mono"
                              >
                                "{sample}"
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}
